/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  IDivergence,
  IDivergenceCreate,
  IDivergenceParams,
  IDivergenceUpdate,
  IDivergenceStoreJustificationUpdate,
  IDivergenceDelete,
  DivergenceBatchUpdateDto,
  IPayrollDiscountDivergenceParams,
  IBatchIdentityPreviewItem,
  IManualIdentityPreview,
  IManualIdentityPreviewRequest,
  IUpdateDivergenceStatusRequest,
  IUpdateDivergenceStatusResponse,
  IBatchDecisionResult,
  IPayrollDiscountPage,
  IReversalResult,
  IReversalBatchResult,
} from "src/@types/divergence";
import { Paginated } from "src/@types/paginated";
import { Tags } from "../enum/tags";
import { apiSlice } from "./http";

export const divergenceInformations = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    createDivergence: builder.mutation<string, IDivergenceCreate>({
      query: (divergence: IDivergenceCreate) => ({
        url: "/divergence",
        method: "POST",
        body: divergence, 
      }),
      invalidatesTags: [
        Tags.DIVERGENCE,
        Tags.STORE_CONCILIATION,
        Tags.CAN_CLOSE_CONCILIATION,
        Tags.CONCILIATION_HISTORY,
      ],
    }),
    updateDivergence: builder.mutation<string, IDivergenceUpdate>({
      query: (divergence: IDivergenceUpdate) => ({
        url: `/divergence/${divergence.id}`,
        method: "PUT",
        body: divergence,
      }),
      invalidatesTags: [
        Tags.DIVERGENCE,
        Tags.STORE_CONCILIATION,
        Tags.CAN_CLOSE_CONCILIATION,
        Tags.CONCILIATION_HISTORY,
      ],
    }),
    updateDivergenceStoreJustification: builder.mutation<
      { divergence: any; historyRecorded: boolean },
      IDivergenceStoreJustificationUpdate
    >({
      query: (divergence: IDivergenceStoreJustificationUpdate) => ({
        url: `/divergence/${divergence.id}/store-justification`,
        method: "PATCH",
        body: divergence,
      }),
      invalidatesTags: [
        Tags.DIVERGENCE,
        Tags.STORE_CONCILIATION,
        Tags.CAN_CLOSE_CONCILIATION,
        Tags.CONCILIATION_HISTORY,
      ],
    }),
    publicUpdateDivergenceStoreJustification: builder.mutation<
      { divergence: any; historyRecorded: boolean },
      IDivergenceStoreJustificationUpdate
    >({
      query: (divergence: IDivergenceStoreJustificationUpdate) => ({
        url: `/public/divergence/${divergence.id}/store-justification`,
        method: "PATCH",
        body: divergence,
      }),
      invalidatesTags: [
        Tags.DIVERGENCE,
        Tags.STORE_CONCILIATION,
        Tags.CAN_CLOSE_CONCILIATION,
        Tags.CONCILIATION_HISTORY,
      ],
    }),
    getDivergence: builder.query<Paginated<IDivergence>, IDivergenceParams>({
      query: ({
        search,
        meta,
        is_back_office_origin,
        conciliation_store_id,
        is_loss,
        is_evidence,
        need_doc,
        justification,
        divergence,
        orderBy,
        justificationSearch,
        store_code,
        is_rejected,
        solicitation_id,
        dates,
        include_email_status,
      }) => {
        const params = new URLSearchParams({
          search,
          ...(is_back_office_origin !== undefined && {
            is_back_office_origin: is_back_office_origin?.toString(),
          }),
          ...(conciliation_store_id !== undefined && {
            conciliation_store_id: conciliation_store_id?.toString(),
          }),
          ...(is_loss !== undefined && {
            is_loss: is_loss?.toString(),
          }),
          ...(is_evidence !== undefined && {
            is_evidence: is_evidence?.toString(),
          }),
          ...(need_doc !== undefined && {
            need_doc: need_doc?.toString(),
          }),
          ...(justification !== undefined && { justification }),
          ...(divergence !== undefined && { divergence }),
          ...(orderBy !== undefined && { orderBy }),
          ...(justificationSearch !== undefined && { justificationSearch }),
          ...(store_code !== undefined && { store_code }),
          ...(dates && dates.length > 0 && { dates: dates.join(",") }),
          ...(is_rejected !== undefined && { is_rejected }),
          ...(solicitation_id !== undefined && { solicitation_id }),
          ...(include_email_status !== undefined && { include_email_status }),
          page: meta.page.toString(),
          perPage: "1000",
        });

        return {
          url: `/divergence?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.DIVERGENCE],
    }),
    publicGetDivergence: builder.query<
      Paginated<IDivergence>,
      IDivergenceParams
    >({
      query: ({
        search,
        meta,
        is_back_office_origin,
        conciliation_store_id,
        is_loss,
        is_evidence,
        need_doc,
        justification,
        divergence,
        orderBy,
        justificationSearch,
        store_code,
        is_rejected,
        solicitation_id,
        dates,
      }) => {
        const params = new URLSearchParams({
          search,
          ...(is_back_office_origin !== undefined && {
            is_back_office_origin: is_back_office_origin?.toString(),
          }),
          ...(conciliation_store_id !== undefined && {
            conciliation_store_id: conciliation_store_id?.toString(),
          }),
          ...(is_loss !== undefined && {
            is_loss: is_loss?.toString(),
          }),
          ...(is_evidence !== undefined && {
            is_evidence: is_evidence?.toString(),
          }),
          ...(need_doc !== undefined && {
            need_doc: need_doc?.toString(),
          }),
          ...(justification !== undefined && { justification }),
          ...(divergence !== undefined && { divergence }),
          ...(orderBy !== undefined && { orderBy }),
          ...(justificationSearch !== undefined && { justificationSearch }),
          ...(store_code !== undefined && { store_code }),
          ...(dates && dates.length > 0 && { dates: dates.join(",") }),
          ...(is_rejected !== undefined && { is_rejected }),
          ...(solicitation_id !== undefined && { solicitation_id }),
          page: meta.page.toString(),
          perPage: "1000",
        });

        return {
          url: `/public/divergence?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.DIVERGENCE],
    }),
    updateDivergenceBatch: builder.mutation<void, DivergenceBatchUpdateDto>({
      query: (values) => ({
        url: `/divergence`,
        method: "PUT",
        body: { divergences: values },
      }),
      invalidatesTags: [Tags.DIVERGENCE, Tags.CONCILIATION_HISTORY],
    }),
    conciliateDivergences: builder.mutation<
      void,
      { divergences: Array<{ id: number; justification_id?: number }> }
    >({
      query: ({ divergences }) => ({
        url: `/divergence/conciliation`,
        method: "POST",
        body: { divergences },
      }),
      invalidatesTags: [
        Tags.DIVERGENCE,
        Tags.STORE_CONCILIATION,
        Tags.CAN_CLOSE_CONCILIATION,
        Tags.CONCILIATION_HISTORY,
      ],
    }),
    getConciliatedHistoryByDivergence: builder.query<
      any,
      { divergenceId: number }
    >({
      query: ({ divergenceId }) => {
        return {
          url: `/divergence/conciliation/history/${divergenceId}`,
          method: "GET",
        };
      },
      providesTags: [
        Tags.DIVERGENCE,
        Tags.STORE_CONCILIATION,
        Tags.CAN_CLOSE_CONCILIATION,
      ],
    }),
    softDeleteDivergence: builder.mutation<void, IDivergenceDelete>({
      query: ({ id, version }) => ({
        url: `/divergence/${id}`,
        method: "DELETE",
        body: { version },
      }),
      invalidatesTags: [
        Tags.DIVERGENCE,
        Tags.STORE_CONCILIATION,
        Tags.CAN_CLOSE_CONCILIATION,
        Tags.CONCILIATION_HISTORY,
      ],
    }),
    getPayrollDiscountDivergences: builder.query<
      IPayrollDiscountPage,
      IPayrollDiscountDivergenceParams
    >({
      query: ({ search, conciliation_date_start, conciliation_date_end, store_code, analyst, status, justification_id, type, payroll_voucher_status, resubmission, orderBy, perPage, meta }) => {
        const params = new URLSearchParams({
          search,
          page: meta.page.toString(),
          perPage: perPage.toString(),
        });

        if (conciliation_date_start) {
          params.append('conciliation_date_start', conciliation_date_start);
        }

        if (conciliation_date_end) {
          params.append('conciliation_date_end', conciliation_date_end);
        }

        if (store_code) {
          params.append('store_code', store_code);
        }

        if (analyst) {
          params.append('analyst', analyst);
        }

        if (status && status.length > 0) {
          params.append('status', status.join(','));
        }

        if (justification_id) {
          params.append('justification_id', justification_id);
        }

        if (type) {
          params.append('type', type);
        }

        if (payroll_voucher_status) {
          params.append('payroll_voucher_status', payroll_voucher_status);
        }

        if (resubmission) {
          params.append('resubmission', 'true');
        }

        if (orderBy) {
          params.append("orderBy", orderBy);
        }

        return {
          url: `/divergence/payroll-discount?${params.toString()}`,
          method: "GET",
        };
      },
      providesTags: [Tags.DIVERGENCE],
    }),
    updateDivergenceStatus: builder.mutation<
      IUpdateDivergenceStatusResponse,
      IUpdateDivergenceStatusRequest
    >({
      query: ({
        id,
        status,
        reason,
        installments_count,
        manual_identity_cpf,
        manual_identity_operator_code,
      }) => ({
        url: "/divergence/payroll-discount/status",
        method: "PUT",
        body: {
          id,
          status,
          reason,
          installments_count,
          ...(manual_identity_cpf ? { manual_identity_cpf } : {}),
          ...(manual_identity_operator_code
            ? { manual_identity_operator_code }
            : {}),
        },
      }),
      invalidatesTags: [Tags.DIVERGENCE, Tags.CONCILIATION_HISTORY],
    }),

    previewManualIdentity: builder.query<
      IManualIdentityPreview,
      IManualIdentityPreviewRequest
    >({
      query: ({ divergence_id, cpf, operator_code }) => {
        const params = new URLSearchParams({
          divergence_id: String(divergence_id),
        });

        if (cpf) {
          params.append("cpf", cpf);
        }

        if (operator_code) {
          params.append("operator_code", operator_code);
        }

        return {
          url: `/divergence/payroll-discount/identity-preview?${params.toString()}`,
          method: "GET",
        };
      },
    }),
    previewIdentitiesBatch: builder.query<
      IBatchIdentityPreviewItem[],
      { divergence_ids: number[] }
    >({
      query: ({ divergence_ids }) => ({
        url: "/divergence/payroll-discount/identity-preview/batch",
        method: "POST",
        body: { divergence_ids },
      }),
    }),
    batchUpdateDivergenceStatus: builder.mutation<
      IBatchDecisionResult,
      {
        items: {
          id: number;
          status: string;
          reason?: string;
          installments_count?: number;
        }[];
      }
    >({
      query: ({ items }) => ({
        url: "/divergence/payroll-discount/status/batch",
        method: "PUT",
        body: { items },
      }),
      invalidatesTags: [Tags.DIVERGENCE, Tags.CONCILIATION_HISTORY],
    }),
    reverseDivergenceBatch: builder.mutation<
      IReversalBatchResult,
      { divergenceIds: number[]; reason: string }
    >({
      query: ({ divergenceIds, reason }) => ({
        url: "/divergence/payroll-discount/reversal/batch",
        method: "POST",
        body: { divergenceIds, reason },
      }),
      invalidatesTags: [Tags.DIVERGENCE, Tags.CONCILIATION_HISTORY],
    }),
    reverseDivergence: builder.mutation<
      IReversalResult,
      { id: number; reason: string; version: number }
    >({
      query: ({ id, reason, version }) => ({
        url: `/divergence/payroll-discount/${id}/reversal`,
        method: "POST",
        body: { reason, version },
      }),
      invalidatesTags: [Tags.DIVERGENCE, Tags.CONCILIATION_HISTORY],
    }),
  }),
});

export const {
  useGetDivergenceQuery,
  useCreateDivergenceMutation,
  useUpdateDivergenceMutation,
  usePublicGetDivergenceQuery,
  useUpdateDivergenceStoreJustificationMutation,
  useUpdateDivergenceBatchMutation,
  usePublicUpdateDivergenceStoreJustificationMutation,
  useConciliateDivergencesMutation,
  useGetConciliatedHistoryByDivergenceQuery,
  useSoftDeleteDivergenceMutation,
  useGetPayrollDiscountDivergencesQuery,
  useUpdateDivergenceStatusMutation,
  useLazyPreviewManualIdentityQuery,
  usePreviewManualIdentityQuery,
  usePreviewIdentitiesBatchQuery,
  useBatchUpdateDivergenceStatusMutation,
  useReverseDivergenceMutation,
  useReverseDivergenceBatchMutation,
} = divergenceInformations;

export const {
  endpoints: {
    getDivergence: { select: selectGetDivergece },
    createDivergence: { select: selectCreateDivergece },
    updateDivergence: { select: selectUpdateDivergece },
    updateDivergenceBatch: { select: selectUpdateDivergenceBatch },
  },
} = divergenceInformations;
